import type { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, MediaOriginTag } from '@mustauto/ui/web';
import type { BeforeAfterPair } from '@mustauto/utils';

interface PairPhoto {
  id: string;
  url: string;
  /** `'gallery'` = escolhida na galeria do aparelho (selo "Galeria"). */
  source?: string | null;
  takenAt?: string | null;
}

/**
 * Antes/depois da estética (F2.4) no detalhe da OS do painel: o par de cada
 * peça, lado a lado. Leitura — quem fotografa é o detailer, no aparelho, com a
 * foto do antes por cima para alinhar o depois.
 */
export function OSBeforeAfterCard<T extends PairPhoto>({
  pairs,
  renderDownload,
  openedAt,
}: {
  pairs: BeforeAfterPair<T>[];
  /** Criação da OS — o selo da galeria diz quando a foto é de dias antes. */
  openedAt?: string | null;
  /** Botão de baixar de cada foto — o mesmo do resto da mídia da OS. */
  renderDownload: (photo: T, label: string) => ReactNode;
}) {
  if (pairs.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles size={16} aria-hidden />
          Antes e depois ({pairs.length} {pairs.length === 1 ? 'peça' : 'peças'})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {pairs.map((pair) => (
          <div key={pair.panel}>
            <p className="mb-2 text-sm font-medium">{pair.label}</p>
            <div className="grid grid-cols-2 gap-3">
              {(['before', 'after'] as const).map((role) => {
                const photo = pair[role];
                const label = role === 'before' ? 'Antes' : 'Depois';
                return (
                  <figure key={role} className="m-0">
                    {photo ? (
                      <div className="group relative aspect-[3/4] overflow-hidden rounded-lg border bg-muted">
                        <a href={photo.url} target="_blank" rel="noreferrer" aria-label={`Abrir ${label.toLowerCase()} · ${pair.label}`}>
                          <img src={photo.url} alt={`${label} · ${pair.label}`} className="h-full w-full object-cover" loading="lazy" />
                        </a>
                        <MediaOriginTag source={photo.source} takenAt={photo.takenAt} openedAt={openedAt} />
                        {renderDownload(photo, `${label.toLowerCase()} · ${pair.label}`)}
                      </div>
                    ) : (
                      <div className="flex aspect-[3/4] items-center justify-center rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
                        {role === 'after' ? 'Ainda não fotografado' : 'Sem foto'}
                      </div>
                    )}
                    <figcaption className="mt-1 text-xs font-medium text-muted-foreground">{label}</figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
